import React,{useEffect,useState} from "react";
import{Box,Button,Checkbox,Dialog,DialogContent,FormControl,FormControlLabel,FormGroup,IconButton,InputAdornment,InputLabel,MenuItem,Select,Stack,TextField,Tooltip,Typography}from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import{useDashboardTheme}from "../../../context/DashboardThemeContext";
import{useAlert}from "../../../context/AlertContext";

const API_BASE="/api/persons";
const emptyForm={tipo:"N",cedula:"",ruc:"",razon_social:"",nombre_comercial:"",telefono:"",correo:"",direccion:"",es_cliente:true,es_proveedor:false,es_empleado:false,es_extranjero:false};

function getToken(){
  return localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
}

export default function Persons({open,person=null,onClose,onSaved,initialValues}){
  const{dashboardTheme}=useDashboardTheme();
  const{showSuccess,showError,showInfo}=useAlert();
  const[form,setForm]=useState(emptyForm);
  const[saving,setSaving]=useState(false);
  const[searchingSri,setSearchingSri]=useState(false);
  const token=getToken();
  const isEdit=Boolean(person);

  useEffect(()=>{
    if(!open)return;
    if(person){
      setForm({tipo:person.tipo||"N",cedula:person.cedula||"",ruc:person.ruc||"",razon_social:person.razon_social||"",nombre_comercial:person.nombre_comercial||person.razon_social||"",telefono:person.telefono||"",correo:person.correo||"",direccion:person.direccion||"",es_cliente:Boolean(person.es_cliente),es_proveedor:Boolean(person.es_proveedor),es_empleado:Boolean(person.es_empleado),es_extranjero:Boolean(person.es_extranjero)});
      return;
    }
    setForm({...emptyForm,...(initialValues||{}),tipo:initialValues?.tipo||"N",es_cliente:typeof initialValues?.es_cliente==="boolean"?initialValues.es_cliente:true,es_proveedor:typeof initialValues?.es_proveedor==="boolean"?initialValues.es_proveedor:false,es_empleado:typeof initialValues?.es_empleado==="boolean"?initialValues.es_empleado:false,es_extranjero:typeof initialValues?.es_extranjero==="boolean"?initialValues.es_extranjero:false});
  },[open,person?.id]);

  const handleChange=(field,value)=>{
    setForm(prev=>({...prev,[field]:value}));
  };

  const closeMainModal=()=>{
    if(saving||searchingSri)return;
    onClose?.();
  };

  const parseBackendError=data=>{
    const error=data?.error||data?.detail||data?.mensaje;
    if(!error)return"No se pudo procesar la solicitud.";
    if(typeof error==="string")return error;
    if(typeof error==="object")return Object.values(error).flat().join(" ");
    return"No se pudo procesar la solicitud.";
  };

  const consultarSri=async()=>{
    const cedula=form.cedula.trim();
    if(!cedula){
      showInfo("Ingrese una cédula para consultar en el SRI.");
      return;
    }
    if(!/^\d{10}$/.test(cedula)){
      showInfo("La cédula debe contener 10 dígitos.");
      return;
    }
    try{
      setSearchingSri(true);
      const response=await fetch(`${API_BASE}/consultar-sri/`,{method:"POST",headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"},body:JSON.stringify({identificacion:cedula})});
      const data=await response.json().catch(()=>({}));
      if(!response.ok||!data.encontrado){
        showInfo(data.mensaje||parseBackendError(data)||"No se encontraron datos en el SRI. Complete la información manualmente.");
        return;
      }
      const sri=data.persona||{};
      setForm(prev=>({...prev,tipo:sri.tipo||prev.tipo,cedula:sri.cedula||prev.cedula,ruc:sri.ruc||prev.ruc,razon_social:sri.razon_social||prev.razon_social,nombre_comercial:sri.razon_social||prev.nombre_comercial}));
      showSuccess(data.mensaje||"Datos encontrados en el SRI.");
    }catch{
      showInfo("No se pudo consultar el SRI. Puede completar los datos manualmente.");
    }finally{
      setSearchingSri(false);
    }
  };

  const validateForm=()=>{
    if(!form.razon_social.trim()){
      showInfo("La razón social es obligatoria.");
      return false;
    }
    if(!form.es_cliente&&!form.es_proveedor&&!form.es_empleado&&!form.es_extranjero){
      showInfo("Selecciona al menos un rol para la persona.");
      return false;
    }
    return true;
  };

  const savePerson=async()=>{
    if(!validateForm())return;
    try{
      setSaving(true);
      const response=await fetch(isEdit?`${API_BASE}/${person.id}/`:`${API_BASE}/crear/`,{method:isEdit?"PUT":"POST",headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"},body:JSON.stringify({tipo:form.tipo||"N",cedula:form.cedula,ruc:form.ruc,razon_social:form.razon_social,nombre_comercial:form.nombre_comercial||form.razon_social,telefono:form.telefono,correo:form.correo,direccion:form.direccion,es_cliente:Boolean(form.es_cliente),es_proveedor:Boolean(form.es_proveedor),es_empleado:Boolean(form.es_empleado),es_extranjero:Boolean(form.es_extranjero)})});
      const data=await response.json();
      if(!response.ok){
        showError(parseBackendError(data));
        return;
      }
      showSuccess(data.mensaje||(isEdit?"Persona actualizada correctamente.":"Persona creada correctamente."));
      onSaved?.(data);
    }catch{
      showError("Error de conexión.");
    }finally{
      setSaving(false);
    }
  };

  const outlinedButtonSx={minHeight:40,px:2.4,borderRadius:"12px",textTransform:"none",fontWeight:700,color:dashboardTheme.button_color,borderColor:dashboardTheme.button_color,boxShadow:"none","&:hover":{borderColor:dashboardTheme.button_color,bgcolor:`${dashboardTheme.button_color}12`,boxShadow:"none"}};
  const roleLabelSx={"& .MuiFormControlLabel-label":{color:dashboardTheme.text_color,fontSize:14,fontWeight:500}};

  return(
    <Dialog open={open} onClose={closeMainModal} fullWidth maxWidth="md" PaperProps={{sx:{borderRadius:"16px",bgcolor:dashboardTheme.card_background,border:`1px solid ${dashboardTheme.border_color}`,boxShadow:"none"}}}>
      <DialogContent sx={{p:{xs:2,sm:2.5,md:3}}}>
        <Stack spacing={2.5}>
          <Typography sx={{color:dashboardTheme.primary_color,fontSize:{xs:22,md:26},fontWeight:600,lineHeight:1.2}}>{isEdit?"Editar persona":"Nueva Persona"}</Typography>
          <Stack direction={{xs:"column",md:"row"}} spacing={2}>
            <FormControl fullWidth size="small" sx={dashboardTheme.inputStyles}>
              <InputLabel>Tipo de persona</InputLabel>
              <Select label="Tipo de persona" value={form.tipo} onChange={event=>handleChange("tipo",event.target.value)}>
                <MenuItem value="N">Natural</MenuItem>
                <MenuItem value="J">Jurídica</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth size="small" label="Cédula" value={form.cedula} onChange={event=>handleChange("cedula",event.target.value.replace(/\D/g,"").slice(0,10))} onKeyDown={event=>{if(event.key==="Enter")consultarSri();}} sx={dashboardTheme.inputStyles} InputProps={{endAdornment:<InputAdornment position="end"><Tooltip title="Buscar en el SRI"><span><IconButton onClick={consultarSri} disabled={searchingSri||saving||!form.cedula.trim()} edge="end" sx={{color:dashboardTheme.button_color}}><SearchRoundedIcon/></IconButton></span></Tooltip></InputAdornment>}}/>
            <TextField fullWidth size="small" label="RUC" value={form.ruc} onChange={event=>handleChange("ruc",event.target.value.replace(/\D/g,"").slice(0,13))} sx={dashboardTheme.inputStyles}/>
          </Stack>
          <TextField fullWidth size="small" label="Razón social" value={form.razon_social} onChange={event=>handleChange("razon_social",event.target.value)} sx={dashboardTheme.inputStyles}/>
          <Stack direction={{xs:"column",md:"row"}} spacing={2}>
            <TextField fullWidth size="small" label="Teléfono" value={form.telefono} onChange={event=>handleChange("telefono",event.target.value)} sx={dashboardTheme.inputStyles}/>
            <TextField fullWidth size="small" label="Correo" type="email" value={form.correo} onChange={event=>handleChange("correo",event.target.value)} sx={dashboardTheme.inputStyles}/>
          </Stack>
          <TextField fullWidth size="small" label="Dirección" multiline minRows={2} value={form.direccion} onChange={event=>handleChange("direccion",event.target.value)} sx={dashboardTheme.inputStyles}/>
          <Box sx={{border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.dashboard_background,borderRadius:"12px",p:{xs:1.5,md:2}}}>
            <Typography sx={{mb:1,color:dashboardTheme.text_color,fontSize:14,fontWeight:600}}>Roles</Typography>
            <FormGroup row sx={{gap:{xs:.5,md:1.5}}}>
              <FormControlLabel sx={roleLabelSx} control={<Checkbox checked={form.es_cliente} onChange={event=>handleChange("es_cliente",event.target.checked)} sx={{color:dashboardTheme.button_color,"&.Mui-checked":{color:dashboardTheme.button_color}}}/>} label="Cliente"/>
              <FormControlLabel sx={roleLabelSx} control={<Checkbox checked={form.es_proveedor} onChange={event=>handleChange("es_proveedor",event.target.checked)} sx={{color:dashboardTheme.button_color,"&.Mui-checked":{color:dashboardTheme.button_color}}}/>} label="Proveedor"/>
              <FormControlLabel sx={roleLabelSx} control={<Checkbox checked={form.es_empleado} onChange={event=>handleChange("es_empleado",event.target.checked)} sx={{color:dashboardTheme.button_color,"&.Mui-checked":{color:dashboardTheme.button_color}}}/>} label="Empleado"/>
              <FormControlLabel sx={roleLabelSx} control={<Checkbox checked={form.es_extranjero} onChange={event=>handleChange("es_extranjero",event.target.checked)} sx={{color:dashboardTheme.button_color,"&.Mui-checked":{color:dashboardTheme.button_color}}}/>} label="Extranjero"/>
            </FormGroup>
          </Box>
          <Box sx={{display:"flex",justifyContent:"flex-end",gap:1,pt:.5}}>
            <Button variant="outlined" onClick={closeMainModal} disabled={saving||searchingSri} sx={outlinedButtonSx}>Cancelar</Button>
            <Button variant="contained" onClick={savePerson} disabled={saving||searchingSri} sx={dashboardTheme.buttonStyles.contained}>{saving?"Guardando...":searchingSri?"Consultando...":"Guardar"}</Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}